/** Small pieces used across screens: status badges, the length meter, empty states, page headers,
 * language-aware text fields and the sticky save bar. */
import { AlertTriangle, CheckCircle2, Circle, CircleDashed, Loader2 } from "lucide-react";
import * as React from "react";
import type { Language, ReelStatus } from "@/api/types";
import { cn } from "@/lib/cn";
import { Button } from "./ui/button";
import { Input, Textarea } from "./ui/form";

export const STATUS_LABEL: Record<ReelStatus, string> = {
  draft: "Draft",
  building: "Building",
  review: "Ready to review",
  published: "Published",
  attention: "Needs attention",
};

export function StatusBadge({ status, progress, className }: { status: ReelStatus; progress?: number | null; className?: string }) {
  const icon = {
    draft: <Circle className="size-3" style={{ color: "var(--s-draft-dot)" }} />,
    building: <Loader2 className="size-3 animate-spin" style={{ color: "var(--s-building-dot)" }} />,
    review: <Circle className="size-3 fill-current" style={{ color: "var(--s-review-dot)" }} />,
    published: <CheckCircle2 className="size-3" style={{ color: "var(--s-published-dot)" }} />,
    attention: <AlertTriangle className="size-3" style={{ color: "var(--s-attention-dot)" }} />,
  }[status];
  return (
    <span
      className={cn("inline-flex h-5 items-center gap-1 rounded-full px-2 text-xs font-medium whitespace-nowrap", className)}
      style={{ background: `var(--s-${status}-bg)`, color: `var(--s-${status}-fg)` }}
    >
      {icon}
      {STATUS_LABEL[status]}
      {status === "building" && progress != null && progress > 0 && (
        <span className="tnum">{Math.round(progress * 100)}%</span>
      )}
    </span>
  );
}

export function LengthMeter({
  total,
  limit,
  exact,
  compact,
}: {
  total: number;
  limit: number;
  exact: boolean;
  compact?: boolean;
}) {
  const over = total > limit + 1e-4;
  const pct = Math.min(100, (total / Math.max(1, limit)) * 100);
  return (
    <div className={cn("flex items-center gap-2", compact ? "text-xs" : "text-sm")}>
      <span className={cn("tnum font-medium", over ? "text-danger-text" : "text-fg")} title={exact ? "Exact" : "Estimate"}>
        {exact ? "" : "≈"}
        {total.toFixed(1)}s
      </span>
      <div className="h-1.5 w-full min-w-10 overflow-hidden rounded-full bg-active" aria-hidden>
        <div className={cn("h-full rounded-full", over ? "bg-danger" : "bg-subtle")} style={{ width: `${pct}%` }} />
      </div>
      <span className="tnum text-subtle whitespace-nowrap">/ {limit}s</span>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  children,
  action,
  className,
}: {
  icon?: React.ReactNode;
  title: string;
  children?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col items-center justify-center gap-2 px-6 py-16 text-center", className)}>
      {icon && <div className="mb-1 text-subtle [&_svg]:size-8">{icon}</div>}
      <h3 className="text-lg font-semibold">{title}</h3>
      {children && <div className="max-w-sm text-sm text-muted">{children}</div>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

export function PageHeader({
  title,
  description,
  actions,
  children,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div className="border-b border-border bg-bg px-6 pb-3 pt-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
          {description && <p className="mt-0.5 text-sm text-muted">{description}</p>}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
      {children && <div className="mt-3">{children}</div>}
    </div>
  );
}

/** Text input / textarea for reel content: the language tag picks the Devanagari font, and spellcheck
 * stays off (browsers can't spellcheck Marathi or Hindi). */
export const ContentInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement> & { lang: Language }>(
  ({ lang, className, ...props }, ref) => (
    <Input ref={ref} lang={lang} spellCheck={lang === "en"} className={cn(lang !== "en" && "text-[15px]", className)} {...props} />
  ),
);
ContentInput.displayName = "ContentInput";

export const ContentTextarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & { lang: Language }
>(({ lang, className, ...props }, ref) => (
  <Textarea ref={ref} lang={lang} spellCheck={lang === "en"} className={cn(lang !== "en" && "text-[15px]", className)} {...props} />
));
ContentTextarea.displayName = "ContentTextarea";

export function SaveBar({
  dirty,
  saving,
  onSave,
  onDiscard,
  label = "Unsaved changes",
}: {
  dirty: boolean;
  saving?: boolean;
  onSave: () => void;
  onDiscard: () => void;
  label?: string;
}) {
  if (!dirty) return null;
  return (
    <div className="sticky bottom-0 z-20 border-t border-border bg-surface/95 backdrop-blur">
      <div className="flex items-center justify-between gap-3 px-6 py-2.5">
        <span className="flex items-center gap-2 text-sm text-muted">
          <CircleDashed className="size-4 text-warn-text" /> {label}
        </span>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onDiscard} disabled={saving}>
            Discard
          </Button>
          <Button variant="primary" onClick={onSave} loading={saving}>
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

export function Section({
  title,
  description,
  children,
  className,
  actions,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  actions?: React.ReactNode;
}) {
  return (
    <section className={cn("rounded-lg border border-border bg-surface", className)}>
      <header className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div>
          <h2 className="text-base font-semibold">{title}</h2>
          {description && <p className="mt-0.5 text-xs text-muted">{description}</p>}
        </div>
        {actions}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

export function Banner({
  tone = "info",
  children,
  action,
  icon,
}: {
  tone?: "info" | "warn" | "danger" | "ok";
  children: React.ReactNode;
  action?: React.ReactNode;
  icon?: React.ReactNode;
}) {
  const styles = {
    info: "bg-accent-subtle text-accent-text",
    warn: "bg-warn-subtle text-warn-text",
    danger: "bg-danger-subtle text-danger-text",
    ok: "bg-ok-subtle text-ok-text",
  }[tone];
  return (
    <div role={tone === "danger" ? "alert" : "status"} className={cn("flex items-center gap-3 px-4 py-2 text-sm", styles)}>
      {icon && <span className="shrink-0 [&_svg]:size-4">{icon}</span>}
      <div className="min-w-0 flex-1">{children}</div>
      {action}
    </div>
  );
}

export function relTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 45) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400 && d.getDate() === new Date().getDate()) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (s < 7 * 86400) return d.toLocaleDateString([], { weekday: "short", hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString([], { day: "numeric", month: "short" });
}

export function fmtDate(iso: string): string {
  const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""));
  return d.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });
}

export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function initials(name: string): string {
  return (
    name
      .split(/[\s@._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]!.toUpperCase())
      .join("") || "?"
  );
}

export function Avatar({ name, className }: { name: string | null | undefined; className?: string }) {
  const n = name || "?";
  let h = 0;
  for (const ch of n) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return (
    <span
      title={n}
      className={cn("inline-flex size-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white", className)}
      style={{ background: `hsl(${h} 45% 48%)` }}
    >
      {initials(n)}
    </span>
  );
}

/** Shown to members on pages only a newspaper's admins can use. */
export function AdminsOnly({ what }: { what: string }) {
  return (
    <EmptyState title={`Only this newspaper's admins can change ${what}`} className="h-full">
      Ask one of its admins (or a super admin) if something needs changing.
    </EmptyState>
  );
}
